import { Grid } from '@mui/material';
import React from 'react';
import {
  Button,
  UploadedDocument,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import InfoBar from '../../../atoms/InfoBar';
import ContractReviewRow, {
  ReviewDoc,
  ServiceAgreementOption,
} from './ContractReviewRow';
import ReplayIcon from '@mui/icons-material/Replay';
import { makeStyles } from 'tss-react/mui';

interface Props {
  readyDocs: ReviewDoc[];
  processingDocs: any[];
  failedDocs: any[];
  counts: {
    processing: number;
    ready_to_review: number;
    failed: number;
    total: number;
  };
  serviceAgreements: ServiceAgreementOption[];
  healthcarePartners: string[];
  docTypeOptions: string[];
  onFieldChange: (
     
    documentId: string,
     
    field: keyof ReviewDoc,
     
    value: string,
  ) => void;
   
  onPartnerInput: (documentId: string, value: string) => void;
  retryingId?: string | null;
  retryingAll?: boolean;
   
  onRetryOne: (documentId: string) => void;
  onRetryAll: () => void;
  onCreateContracts: () => void;
  uploadZone: React.ReactNode;
  validationErrors?: Record<string, string[]>;
}

const useStyles = makeStyles()({
  wrap: { width: '100%' },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  columns: {
    display: 'flex',
    gap: '12px',
    padding: '0 0 8px',
    '@media (max-width: 1100px)': { display: 'none' }, // headers hide when rows wrap
  },
  colLabel: { fontSize: '12px' },
  docRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 0',
    borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
  },
  readyChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: patientAccessTheme.palette.AIQChips?.background2 || '#E7F6EC',
    color: patientAccessTheme.palette.AIQChips?.textColor2 || '#1B7F3B',
    fontSize: '12px',
    padding: '3px 10px',
    borderRadius: '4px',
    fontFamily: 'Inter',
  },
  procRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 0',
    borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
  },
  procChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: patientAccessTheme.palette.AIQChips?.background3 || '#E8EBFF',
    color: patientAccessTheme.palette.chips.textColor3,
    fontSize: '12px',
    padding: '4px 12px',
    borderRadius: '4px',
    fontFamily: 'Inter',
  },
  failedChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontFamily: 'Inter',
    background: patientAccessTheme.palette.AIQChips?.background1 || '#FDECEA',
    color: patientAccessTheme.palette.chips?.textColor4 || '#D6545E',
    fontSize: '12px',
    padding: '4px 12px',
    borderRadius: '4px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '20px',
    '@media (max-width: 768px)': { flexDirection: 'column' },
  },
  retryBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    border: 'none',
    background: 'transparent',
    color: patientAccessTheme.palette.primary[500],
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    padding: '6px 8px',
    borderRadius: '4px',
    transition: 'background 0.15s ease',
    '&:hover': {
      background: patientAccessTheme.palette.AIQChips?.background2 || '#EEF1FF',
    },
    '&:disabled': {
      color: patientAccessTheme.palette.text.disabled || '#9AA0AC',
      cursor: 'default',
      background: 'transparent',
    },
  },

  refreshIcon: {
    width: '14px',
    height: '14px',
    flexShrink: 0,
  },
  refreshIconSpin: {
    width: '14px',
    height: '14px',
    flexShrink: 0,
    animation: '$spin 0.7s linear infinite',
  },

  sectionGap: { marginTop: '20px' },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
});

const ContractsReview: React.FC<Props> = ({
  readyDocs,
  processingDocs,
  failedDocs,
  counts,
  serviceAgreements,
  healthcarePartners,
  docTypeOptions,
  onFieldChange,
  onPartnerInput,
  onCreateContracts,
  uploadZone,
  validationErrors,
  retryingAll,
  retryingId,
  onRetryAll,
  onRetryOne,
}) => {
  const { classes } = useStyles();
  const hasReady = readyDocs.length > 0;
  const hasProcessing = processingDocs.length > 0;
  const hasFailed = failedDocs.length > 0;

  const columnLabels = [
    'Document',
    'Service Agreement Type',
    'Healthcare Partner',
    'Document Type',
    'Effective Date',
  ];

  return (
    <Grid className={classes.wrap}>
      {/* Ready for Review info} */}
      {hasReady && (
        <Grid style={{ marginBottom: '16px' }}>
          <InfoBar
            type={'Success'}
            width="100%"
            height="100%"
            info={`${readyDocs.length} document${
              readyDocs.length > 1 ? 's are' : ' is'
            } ready for review.`}
          />
        </Grid>
      )}
      {/* Failed-extractions info */}
      {hasFailed && (
        <Grid style={{ marginBottom: '16px' }}>
          <InfoBar
            type="Error"
            width="100%"
            height="100%"
            info={`${failedDocs.length} document${
              failedDocs.length > 1 ? 's' : ''
            } couldn't be processed. Your file is safe — you can retry extraction without re-uploading.`}
          />
        </Grid>
      )}
      {/* Processing Extractions Info*/}
      {hasProcessing && (
        <Grid style={{ marginBottom: '16px' }}>
          <InfoBar
            type="Info"
            width="100%"
            height="100%"
            info={`${processingDocs.length} document${
              processingDocs.length > 1 ? 's are' : ' is'
            } being processed. Please check back in a few minutes for the results.`}
          />
        </Grid>
      )}
      {uploadZone && <Grid style={{ marginBottom: '20px' }}>{uploadZone}</Grid>}
      {/* READY section */}
      {hasReady && (
        <>
          <Grid className={classes.sectionHeader}>
            <Typography
              fontSize="14px"
              fontType="semiBold"
              lineHeight="24px"
              textColor={patientAccessTheme.palette.text.highemphasis}
            >
              Ready for Review
            </Typography>
            <span className={classes.readyChip}>
              ✓ {counts.ready_to_review} ready
            </span>
          </Grid>

          <Grid className={classes.columns}>
            {columnLabels.map((label, i) => (
              <Typography
                key={label}
                fontSize="12px"
                fontType="regular"
                lineHeight="18px"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
                // rough column widths to align with the row fields
                margin={`0px ${['242px', '38px', '70px', '75px'][i]} 0px 0px `}
              >
                {label}
              </Typography>
            ))}
          </Grid>

          {readyDocs.map((doc) => (
            <ContractReviewRow
              key={doc.document_id}
              doc={doc}
              serviceAgreements={serviceAgreements}
              healthcarePartners={healthcarePartners}
              docTypeOptions={docTypeOptions}
              onFieldChange={onFieldChange}
              onPartnerInput={onPartnerInput}
              errorFields={validationErrors?.[doc.document_id] || []}
            />
          ))}
          <Grid className={classes.actions}>
            <Button
              label="Create contracts ↗"
              variant="contained"
              height="37px"
              width="auto"
              disabled={!hasReady}
              onClick={onCreateContracts}
            />
          </Grid>
        </>
      )}

      {/* FAILED docs with per-row retry */}
      {hasFailed && (
        <Grid className={hasReady ? classes.sectionGap : undefined}>
          <Grid className={classes.sectionHeader}>
            <Typography
              fontSize="14px"
              fontType="semiBold"
              lineHeight="24px"
              textColor={patientAccessTheme.palette.text.highemphasis}
            >
              Failed Documents
            </Typography>
            <Button
              label={
                retryingAll ? 'Retrying…' : `Retry all (${failedDocs.length})`
              }
              variant="outlined"
              height="28px"
              width="auto"
              disabled={retryingAll}
              onClick={onRetryAll}
              startIcon={
                <ReplayIcon
                  className={
                    retryingAll ? classes.refreshIconSpin : classes.refreshIcon
                  }
                />
              }
            />
          </Grid>
          {failedDocs.map((d) => {
            const isRetrying = retryingId === d._id || retryingAll;
            return (
              <Grid className={classes.docRow} key={d._id}>
                <UploadedDocument
                  name={d.document_name}
                  size={''}
                  handleDelete={() => {}}
                  width="480px"
                  truncateWidth="400px"
                  hideDelete={true}
                />
                <Grid style={{ flex: 1 }} />
                <span className={classes.failedChip}>⚠ Extraction failed</span>
                <Button
                  label={
                    <Typography
                      fontSize="12px"
                      fontType="medium"
                      textColor={patientAccessTheme.palette.primary.main}
                    >
                      {isRetrying ? 'Retrying…' : 'Retry'}
                    </Typography>
                  }
                  variant="text"
                  height="24px"
                  width="auto"
                  disabled={isRetrying}
                  onClick={() => onRetryOne(d._id)}
                  startIcon={
                    <ReplayIcon
                      className={
                        isRetrying
                          ? classes.refreshIconSpin
                          : classes.refreshIcon
                      }
                    />
                  }
                />
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* STILL PROCESSING section */}
      {hasProcessing && (
        <Grid className={hasFailed ? classes.sectionGap : undefined}>
          <Typography
            fontSize="14px"
            fontType="semiBold"
            lineHeight="24px"
            textColor={patientAccessTheme.palette.text.highemphasis}
            margin="0px 0px 8px 0px"
          >
            Documents Under Process
          </Typography>
          {processingDocs.map((d: any) => (
            <Grid className={classes.procRow} key={d?._id}>
              <UploadedDocument
                name={d?.document_name}
                size={''}
                handleDelete={() => {}}
                width="480px"
                truncateWidth="400px"
                hideDelete={true}
              />
              <Grid style={{ flex: 1 }} />
              <span className={classes.procChip}>⏳ Processing</span>
            </Grid>
          ))}
        </Grid>
      )}
    </Grid>
  );
};

export default ContractsReview;
